<template>
    <div :class="`improvement-info improvement-info--${this.def}`">
        <div v-if="showHeaderImage"
             class="improvement-info__header-image"
             :style="`background-image: url('${this.improvement.imageUrl}');`" />
        <div class="improvement-info__header">
            <h2 class="improvement-info__header__title">
                {{ improvement.title }}
            </h2>
            <div class="improvement-info__header__price">
                <span v-if="!scheme.priceless">{{ improvement.priceDisplay }}</span>
            </div>
        </div>
        <div class="improvement-info__container">
            <hh-msg-display :message="info.message" type="info" />
            <hh-error-display :error="errorMessage" />
            <section class="improvement-info__content">
                <div class="improvement-info__content__primary-wrapper">
                    <metrics-display class="improvement-info__content__savings"
                                     mode="modal"
                                     :use-state="false"
                                     :co2-metric="co2Metric"
                                     :fuel-bill-metric="fuelBillMetric"
                                     :sap-score-metric="sapMetric" />
                    <div v-if="hasDescription"
                         class="improvement-info__content__description-wrapper">
                        <div ref="descriptionContainer"
                             :class="`improvement-info__content__description ${displayAllText ? 'improvement-info__content__description--all' : ''}`"
                             :style="`overflow: ${displayAllText ? '' : 'hidden'};`"
                             v-html="improvement.description" />
                        <div v-if="!displayAllText"
                             class="improvement-info__content__description-mask"
                             @click="onShowMoreClick">
                            <a class="improvement-info__content__description-mask__button"
                               @click="onShowMoreClick">Show More</a>
                        </div>
                    </div>
                    <div class="clear" />
                </div>
            </section>
            <section v-if="hasAlternatives"
                     class="improvement-info__content__alternatives">
                <div class="improvement-info__content__alternatives__wrapper">
                    <h4 class="improvement-info__content__alternatives__title">
                        Alternatives
                    </h4>
                    <p class="improvement-info__content__alternatives__instructions">
                        For more information, click the <i class="fas fa-info" /> button.
                    </p>
                    <ul class="improvement-info__content__alternatives__list">
                        <alternatives-list-item v-for="item in improvement.alternatives"
                                                :key="item.id"
                                                :item="item"
                                                @view-info="onViewInfo(item)" />
                    </ul>
                </div>
            </section>
        </div>
        <div class="improvement-info__buttons --modal-buttons">
            <hh-btn class="improvement-info__button"
                    text="Back"
                    type="cancel"
                    icon-class="fas fa-chevron-circle-left"
                    icon-location="pre"
                    @click="onBackClick" />
        </div>
    </div>
</template>

<script>

import _ from 'lodash'
import Vuex from 'vuex'

import Casing from '@/helpers/casing'

import METRIC_KEYS from '@/models/METRIC_KEYS'

import Improvement from '@/models/improvement'
import Project from '@/models/project'

import AlternativesListItem from '../alternatives-list-item'
import MetricsDisplay from '../metrics-display'
import ImprovementInfo from './improvement-info'

export default {

    name: 'ImprovementInfoModal',

    props: {
        /**
         * @type {string|VueProp}
         */
        def: {
            type: String,
            required: true,
        },

        /**
         * @type {ImprovementInfoModalData|VueProp}
         */
        info: {
            type: Object,
            required: true,
        },

        /**
         * @type {?string|VueProp}
         */
        forceDisplayTestState: {
            type: String,
            default: null,
        },
    },

    data() {
        return {
            /**
             * @type {?{ message: string, linkedId: string|number }}
             */
            error: null,

            /**
             * @type {?boolean}
             */
            displayAllText: null,
        }
    },

    computed: {
        ...Vuex.mapState({
            /**
             * @param {VuexState} stateStore
             * @returns {Project}
             */
            currentProject: stateStore => Project.ensure(stateStore.improvements.currentProject),

            /**
             * @param {VuexState} stateStore
             * @returns {Array}
             */
            savings: stateStore => stateStore.improvements.savings,

            /**
             * @param {VuexState} stateStore
             * @returns {Array<MetricsVuexStateData>}
             */
            baseMetrics: stateStore => stateStore.metrics.data,
        }),

        /**
         * @returns {number}
         */
        backSteps() {
            return this.info.backSteps !== undefined ? this.info.backSteps : -1
        },

        /**
         * @returns {?Metric}
         */
        co2Metric() {
            return this.improvement.metrics
                ? this.improvement.metrics.find(m => m.key === METRIC_KEYS.carbonSaving) : null
        },

        /**
         * @returns {string}
         */
        errorMessage() {
            return this.error
                ? this.error.message
                : this.info.error ? this.info.error : ''
        },

        /**
         * @returns {?Metric}
         */
        fuelBillMetric() {
            return this.improvement.metrics
                ? this.improvement.metrics.find(m => m.key === METRIC_KEYS.fuelBill) : null
        },

        /**
         * @returns {boolean}
         */
        hasAlternatives() {
            return this.improvement.alternatives && this.improvement.alternatives.length > 0
        },

        /**
         * @returns {boolean}
         */
        hasDescription() {
            return Boolean(this.improvement.description && this.improvement.description.length)
        },

        /**
         * @returns {Improvement}
         */
        improvement() {
            return Improvement.ensure(this.info.item)
        },

        /**
         * @returns {?Metric}
         */
        sapMetric() {
            return this.scheme.showSapMetrics && this.improvement.metrics
                ? this.improvement.metrics.find(m => m.key === METRIC_KEYS.sapScore) : null
        },

        /**
         * @returns {Scheme}
         */
        scheme() {
            return this.$scheme
        },

        /**
         * @returns {boolean}
         */
        showHeaderImage() {
            return this.improvement.hasImage && this.improvement.imageUrl && this.improvement.imageUrl.length
        },
    },

    beforeMount() {
        this.error = null
    },

    beforeUpdate() {
        this._clearIrrelevantError()
    },

    mounted() {
        this._initialiseTextDisplay()
    },

    methods: {

        onShowMoreClick() {
            this.displayAllText = true
        },

        onBackClick() {
            this.$eventbus.$emit(this.$appevents.hideModalWithoutActions)
        },

        onViewInfo(item) {
            const key = Casing.kebabCase(item.title)
            history.pushState({ key }, item.title, `#${ key }`)
            const data = {
                item: new Improvement(item, {
                    baseMetrics: this.baseMetrics,
                    projectedMetrics: this._itemSavings(item),
                    alternatives: this._itemAlternatives(item),
                }),
                backSteps: this.backSteps - 1,
            }
            this.$eventbus.$emit(this.$appevents.showModal, ImprovementInfo, {
                data,
                cssModifier: 'info',
                onClose: () => {
                    history.go(this.backSteps)
                },
            })
        },

        /**
         * Clears any errors not pertaining to the active info.
         */
        _clearIrrelevantError() {
            if (this.error && this.info && this.info.item && this.info.item.id !== this.error.linkedId)
                this.error = null
        },

        /**
         * Gets the alternative items to a given item.
         * @param {ImprovementBasics} item
         * @returns {Array<ImprovementBasics>} Alternative improvement data from state.
         */
        _itemAlternatives(item) {
            return this.currentProject.improvements
                .filter(imp => imp.alternatives && imp.alternatives.length &&
                    imp.alternatives.find(alt => alt.id === item.id))
        },

        /**
         * Gets the savings for a given item.
         * @param {ImprovementBasics} item
         * @returns {Array} Savings data from state.
         */
        _itemSavings(item) {
            if (!this.savings || !this.savings.length) return []
            const filteredSavings = this.savings.filter(i => _.isEqual(i.improvementIds, [ item.id ]))
            return filteredSavings && filteredSavings.length ? filteredSavings[0].results : []
        },

        /**
         * Initialises the display text.
         */
        _initialiseTextDisplay() {
            if (this.forceDisplayTestState === 'all')
                this.displayAllText = this.improvement.description && true
            else if (this.forceDisplayTestState === 'short')
                this.displayAllText = false
            else
                this.displayAllText = this.improvement.description && this.$refs.descriptionContainer.clientHeight === this.$refs.descriptionContainer.scrollHeight
        },
    },

    components: {
        AlternativesListItem,
        MetricsDisplay,
    },
}

</script>

<style lang="scss">

// Ensure colours and other visual style elements are added to theme files, not placed in here.
// src\scss\schemes\components\improvements\modals\_improvement-info.scss

.improvement-info {
    padding-bottom: rem(40px);

    @include bp-min-mid {
        padding-bottom: 0;
    }

    @include element('header-image') {
        background-position: center;
        background-repeat: no-repeat;
        background-size: cover;
        height: rem(200px);
        position: relative;
        width: calc(100% + #{rem(20px)});
        right: rem(10px);
        margin: rem(-35px) 0 rem(10px) 0;

        @include bp-min-small {
            width: calc(100% + #{rem(50px)});
            right: rem(25px);
            top: rem(5px);
            margin: rem(-30px) 0 rem(10px) 0;
        }
    }

    @include element('header') {
        margin: 0 0 0.6em 0;
        line-height: 1.2em;
        display: flex;
        flex-wrap: nowrap;
        justify-content: space-between;
        align-items: center;

        @include element('title') {
            margin: 0;

            @include bp-min-mid {
                padding-left: 0;
            }
        }

        @include element('price') {
            font-weight: bold;
            padding-left: rem(20px);
        }
    }

    @include element('container') {
        margin-bottom: 1rem;

        @include bp-min-wide {
            margin-bottom: 0;
        }
    }

    @include element('content') {

        @include element('primary-wrapper') {
            text-align: justify;
            display: inline;

            @include bp-min-mid {
                margin-bottom: rem(10px);
            }
        }

        @include element('savings') {
            margin-bottom: rem(5px);

            @include bp-min-small {
                max-width: rem(400px);
            }

            @include bp-min-mid {
                margin-left: 0;
                display: flex;
                flex-direction: row;
                max-width: 100%;
            }
        }

        @include element('description-wrapper') {
            margin-top: 1rem;
            position: relative;
        }

        @include element('description') {
            max-height: none;

            @include bp-min-small {
                max-height: 13rem;
                margin-bottom: rem(15px);
            }

            @include bp-min-mid {
                max-height: 8.2rem;
            }

            @include modifier('all') {
                max-height: none;
                transition: 0.3s ease-in;
            }

            *:first-child {
                margin-top: 0;
            }

            *:last-child {
                margin-bottom: 0;
            }
        }

        @include element('description-mask') {
            display: none;

            @include bp-min-small {
                display: table;
                background: #fff;
                height: 2.5rem;
                position: absolute;
                bottom: 0;
                width: 100%;
                cursor: pointer;
            }

            @include element('button') {
                display: table-cell;
                text-align: center;
                position: relative;
                top: 1.3rem;
                cursor: pointer;
            }
        }

        @include element('alternatives') {
            font-size: 0.9em;
            margin: rem(15px) 0 rem(-10px) 0;

            @include bp-min-mid {
                margin: 0 rem(-25px);
                padding: rem(15px) rem(17px) 0 rem(17px);
                flex: 1 1 auto;
            }

            @include element('title') {
                margin-bottom: 0;

                @include bp-min-small {
                    padding: 8px 0;
                }

                @include bp-min-mid {
                    padding: 4px 0;
                }
            }

            @include element('instructions') {
                font-size: 0.9rem;
            }

            @include element('list') {
                @include unstyle-list-container;
            }

            @include bp-min-mid {
                flex: 1 1 auto;
            }
        }
    }

    @include element('buttons') {

        @include bp-min-mid {
            display: none !important;
        }
    }

    @include element('button') {
        min-width: 120px;
        font-size: 0.8rem;
    }
}

</style>
